import {
  AvroOptional,
  type RNDevicePropertiesV5,
  RNAppsV5,
  BuildVariant,
  Environment,
} from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNDevicePropertiesV5";
import type { JSTModel } from "@indeed/tare/model/JSTModel";
import { getJSTModelSync } from "@indeed/tare/nativeonly/JSTModel";
import { Platform } from "react-native";

import { type CanonicalEnvironment, isProductionEnvironment } from "../environment";
import { getSyntheticUserAgent } from "../native-modules/tare/getSyntheticUserAgent";
import { getTareBridge } from "../native-modules/tare/getTareBridge";
import { getCommitSha, getReactNativeVersion } from "./getCommitSha";

const model = getJSTModelSync(() => getTareBridge() ?? undefined);

function readSafely<T>(read: () => T): T | null {
  try {
    return read();
  } catch {
    return null;
  }
}

/**
 * Reads each model value once. Telemetry runs inside error reporting, so a host whose bridge throws
 * (e.g. web pages without `globalThis._tareData`) must degrade to defaults instead of throwing.
 */
function readModelSnapshot(): JSTModel {
  const app = readSafely(() => {
    const value = model.app;
    return value && { ...value };
  });
  return {
    user: { accountStatus: readSafely(() => model.user.accountStatus) },
    app,
    device: readSafely(() => model.device),
  };
}

/**
 * Narrow type that only includes the fully qualified variant format.
 * Although RNDevicePropertiesV5 is shared between mobPageLoadInfo and rnHttpError, there generated type is different.
 * We have to use this narrow type to avoid type errors.
 */
type NarrowRNDevicePropertiesV5 = Omit<RNDevicePropertiesV5, "variant" | "env"> & {
  variant?: null | {
    "com.indeed.signals.schema.reactnative.BuildVariant": BuildVariant;
  };
  env?: null | {
    "com.indeed.signals.schema.reactnative.Environment": Environment;
  };
};

/**
 * the version of react native that is running.
 *
 * @returns a semver string representation of the version number
 */
function getRNLibraryVersion(): string {
  return Platform.select({
    web: getReactNativeVersion,
    default: () => {
      const { major, minor, patch } = Platform.constants.reactNativeVersion;
      return `${major}.${minor}.${patch}`;
    },
  })();
}

/**
 * The version of the native app that is running.
 * @returns the native app version, or '0.0.0' when unavailable
 */
function getNativeAppVersion(app: JSTModel["app"]): string {
  return app?.appVersion ?? "0.0.0";
}

/**
 * The version of the react native bundle that is running.
 *
 * Different from the native app version because it takes into account over-the-air updates.
 *
 * @returns a string representing the version of the app
 */
export function getRNAppBundleVersion(): string {
  return getCommitSha();
}

/**
 * Indicate whether we are running on the Job Seeker or Employer app.
 * @returns hardcoded to jsam (job seeker) since it is currently the only client
 */
function getAppName(): RNAppsV5 {
  return RNAppsV5.jsma;
}

/**
 * The version of the operating system in use
 * @returns a string representation of the version (on Android it is a number)
 */
function getOSVersion(): string {
  return `${Platform.Version}`;
}

/**
 * Safely cast a string to BuildVariant enum
 * @param buildConfig the build config string from TARE
 * @returns BuildVariant enum value or undefined if invalid
 */
function castToBuildVariant(
  buildConfig: string | undefined,
): { "com.indeed.signals.schema.reactnative.BuildVariant": BuildVariant } | null {
  if (!buildConfig) {
    return null;
  }

  const lowerConfig = buildConfig.toLowerCase();
  switch (lowerConfig) {
    case "debug":
      return {
        "com.indeed.signals.schema.reactnative.BuildVariant": BuildVariant.debug,
      };
    case "release":
      return {
        "com.indeed.signals.schema.reactnative.BuildVariant": BuildVariant.release,
      };
    default:
      return null;
  }
}

/**
 * Safely cast a CanonicalEnvironment to Environment enum
 * @param buildEnvironment the canonical build environment from TARE
 * @returns Environment enum value or null if undefined
 */
function castToEnvironment(
  buildEnvironment: CanonicalEnvironment | undefined,
): { "com.indeed.signals.schema.reactnative.Environment": Environment } | null {
  if (!buildEnvironment) {
    return null;
  }

  return {
    "com.indeed.signals.schema.reactnative.Environment": isProductionEnvironment(buildEnvironment)
      ? Environment.prod
      : Environment.qa,
  };
}

/**
 * Information that we have access to in react native about the current device.
 *
 * The information that we have access to for android and ios are different so
 * this method provides a union type based on the platform that is running.
 *
 * @returns either android or ios device properties.
 */
export function getDeviceProperties(): NarrowRNDevicePropertiesV5 {
  const snapshot = readModelSnapshot();
  const app = snapshot.app;
  const commonProperties: {
    osVersion: string;
    appName: RNAppsV5;
    nativeAppVersion: string;
    rnLibraryVersion: string;
    rnAppBundleVersion: string;
    userAgent: { string: string } | null;
    variant: { "com.indeed.signals.schema.reactnative.BuildVariant": BuildVariant } | null;
    env: { "com.indeed.signals.schema.reactnative.Environment": Environment } | null;
  } = {
    osVersion: getOSVersion(),
    appName: getAppName(),
    nativeAppVersion: getNativeAppVersion(app),
    rnLibraryVersion: getRNLibraryVersion(),
    rnAppBundleVersion: getRNAppBundleVersion(),
    userAgent: AvroOptional.string(getSyntheticUserAgent(snapshot)),
    variant: castToBuildVariant(app?.buildConfig),
    env: castToEnvironment(app?.buildEnvironment),
  };

  if (Platform.OS === "android") {
    return {
      ...commonProperties,
      platform: Platform.OS,
      manufacturer: AvroOptional.string(Platform.constants.Manufacturer),
      model: AvroOptional.string(Platform.constants.Model),
      brand: AvroOptional.string(Platform.constants.Brand),
      isIpad: null,
    };
  }

  // must be an if statement to determine the iOS specific types
  if (Platform.OS === "ios") {
    return {
      ...commonProperties,
      platform: Platform.OS,
      systemName: AvroOptional.string(Platform.constants.systemName),
      isIpad: { boolean: Platform.isPad },
    };
  }

  if (Platform.OS === "web") {
    const isIpad =
      typeof navigator !== "undefined" &&
      /iPad|Macintosh/.test(navigator.userAgent) &&
      navigator.maxTouchPoints > 1;

    return {
      ...commonProperties,
      platform: Platform.OS,
      isIpad: { boolean: isIpad },
    };
  }

  throw new Error(`Unexpected platform OS: ${Platform.OS}`);
}
